import React from 'react';
import styled from 'styled-components';

const SkillsContainer = styled.section`
  background-color: #4B0000; /* Solid background instead of transparent */
  position: relative;
  overflow: hidden;
  padding: 6rem 2rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(46, 46, 46, 0.85); /* Semi-transparent background */
    z-index: -1;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: clamp(2.2rem, 5vw, 2.8rem);
  font-weight: 700;
  position: relative;
  padding-bottom: 1.5rem;
  letter-spacing: 1px;
  color: #FFD700;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 5px;
    background: linear-gradient(90deg, #FF8C00, #FFA500);
    border-radius: 3px;
    box-shadow: 0 0 12px rgba(255, 140, 0, 0.5);
  }
`;

const SectionIntro = styled.p`
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 4rem;
  font-size: clamp(1rem, 1.8vw, 1.15rem);
  color: #dfad8c;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SkillCard = styled.div`
  background: rgba(75, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 140, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(255, 140, 0, 0.2);
    border-color: #FF8C00;
  }
`;

const SkillCardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #FFD700;
  display: flex;
  align-items: center;
  
  i {
    margin-right: 0.75rem;
    font-size: 1.8rem;
    color: #FF8C00;
  }
`;

const SkillItem = styled.li`
  margin-bottom: 1rem;
  position: relative;
  padding-left: 1.5rem;
  color: #FFFFE0;
  
  &:before {
    content: '▹';
    position: absolute;
    left: 0;
    color: #FF8C00;
  }
`;

const SkillProgress = styled.div`
  margin-top: 0.5rem;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => props.level || '0%'};
    background: linear-gradient(90deg, #FF8C00, #FFA500);
    border-radius: 4px;
    transition: width 1s ease-in-out;
  }
`;

// Add the missing SkillList styled component
const SkillList = styled.ul`
  list-style: none;
  padding: 0;
`;

const Skills = () => {
  return (
    <SkillsContainer id="skills">
      <SectionTitle>My Skills</SectionTitle>
      <SectionIntro>
        I've worked with a variety of technologies in the web development world.
        From front-end design to back-end architecture, here are some of the skills I've acquired along my journey.
      </SectionIntro>
      
      <SkillsGrid>
        <SkillCard>
          <SkillCardTitle>
            <i className="bx bx-code-alt"></i>
            Frontend Development
          </SkillCardTitle>
          <SkillList>
            <SkillItem>
              React.js / Next.js
              <SkillProgress level="90%" />
            </SkillItem>
            <SkillItem>
              JavaScript / TypeScript
              <SkillProgress level="85%" />
            </SkillItem>
            <SkillItem>
              HTML5 / CSS3 / SASS
              <SkillProgress level="95%" />
            </SkillItem>
            <SkillItem>
              Redux / Context API
              <SkillProgress level="80%" />
            </SkillItem>
          </SkillList>
        </SkillCard>
        
        <SkillCard>
          <SkillCardTitle>
            <i className="bx bx-server"></i>
            Backend Development
          </SkillCardTitle>
          <SkillList>
            <SkillItem>
              Node.js / Express
              <SkillProgress level="85%" />
            </SkillItem>
            <SkillItem>
              Python / Django
              <SkillProgress level="75%" />
            </SkillItem>
            <SkillItem>
              RESTful APIs
              <SkillProgress level="90%" />
            </SkillItem>
            <SkillItem>
              GraphQL
              <SkillProgress level="70%" />
            </SkillItem>
          </SkillList>
        </SkillCard>
        
        <SkillCard>
          <SkillCardTitle>
            <i className="bx bx-data"></i>
            Database & DevOps
          </SkillCardTitle>
          <SkillList>
            <SkillItem>
              MongoDB / Mongoose
              <SkillProgress level="80%" />
            </SkillItem>
            <SkillItem>
              PostgreSQL / MySQL
              <SkillProgress level="75%" />
            </SkillItem>
            <SkillItem>
              Docker / Kubernetes
              <SkillProgress level="65%" />
            </SkillItem>
            <SkillItem>
              AWS / Firebase
              <SkillProgress level="70%" />
            </SkillItem>
          </SkillList>
        </SkillCard>
        
        <SkillCard>
          <SkillCardTitle>
            <i className="bx bx-palette"></i>
            Design & Tools
          </SkillCardTitle>
          <SkillList>
            <SkillItem>
              Figma / Adobe XD
              <SkillProgress level="85%" />
            </SkillItem>
            <SkillItem>
              Git / GitHub
              <SkillProgress level="90%" />
            </SkillItem>
            <SkillItem>
              Responsive Design
              <SkillProgress level="95%" />
            </SkillItem>
            <SkillItem>
              UI/UX Principles
              <SkillProgress level="80%" />
            </SkillItem>
          </SkillList>
        </SkillCard>
      </SkillsGrid>
    </SkillsContainer>
  );
};

export default Skills;